<script setup>
import { ref, computed, defineExpose } from "vue";
import { Modal } from "bootstrap";
import { useTaskStore } from "@/stores/taskStore";
import { toast } from "@/utils/toast";

const taskStore = useTaskStore();

const elId = "view_todo";
let modalInst = null;

const loading = ref(false);
const completing = ref(false);
const copied = ref(false);

const task = ref({
  id: null,
  title: "",
  created_at: null,
  due_date: null,
  status: "Pending",
  priority: "Medium",
  description: "",
  tags: [],
  // Both shapes supported:
  assignees: [], // [{id, first_name, last_name, avatar_url}]
  assignee_avatars: [], // [url, url, ...]
  assignee_ids: [], // [number, ...] (optional)
  assignee_names: [], // [string, ...] (optional)
});

/* ---------- helpers ---------- */
function fmtDate(x) {
  if (!x) return "—";
  const d = new Date(x);
  if (isNaN(d)) return String(x);
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
const createdPretty = computed(() => fmtDate(task.value?.created_at));
const duePretty = computed(() => fmtDate(task.value?.due_date));
const isCompleted = computed(() => task.value?.status === "Completed");

const permalink = computed(() => {
  const base = window.location.origin;
  return `${base}/projects/tasks?task=${task.value?.id}`;
});

/** Normalize task shapes from API/store so assignees always render */
function normalizeTask(d = {}) {
  const safeArr = (v) => (Array.isArray(v) ? v : []);

  // Prefer full objects
  let assignees = safeArr(d.assignees).map((a) => ({
    id: a.id ?? null,
    first_name: a.first_name || "",
    last_name: a.last_name || "",
    avatar_url: a.avatar_url || a.avatar || null,
  }));

  // If we don't have objects, try to build from parallel arrays (avatars, ids, names)
  if (!assignees.length) {
    const avatars = safeArr(d.assignee_avatars);
    const ids = safeArr(d.assignee_ids);
    const names = safeArr(d.assignee_names);
    assignees = avatars.map((av, i) => {
      const fullName = names[i] || "";
      let fn = "",
        ln = "";
      if (fullName) {
        const parts = String(fullName).split(" ");
        fn = parts.shift() || "";
        ln = parts.join(" ");
      }
      return {
        id: ids[i] ?? i + 1,
        first_name: fn,
        last_name: ln,
        avatar_url: av || null,
      };
    });
  }

  return {
    id: d.id ?? null,
    title: d.title || "—",
    created_at: d.created_at || null,
    due_date: d.due_date || null,
    status: d.status || "Pending",
    priority: d.priority || "Medium",
    description: d.description || "",
    tags: safeArr(d.tags),
    assignees,
    assignee_avatars: safeArr(d.assignee_avatars),
  };
}

/* ---------- show/hide ---------- */
function show() {
  const el = document.getElementById(elId);
  if (el) {
    modalInst = new Modal(el, { backdrop: "static", keyboard: true });
    modalInst.show();
  }
}
function hide() {
  modalInst?.hide();
}

/* ---------- actions ---------- */
async function open(id) {
  try {
    loading.value = true;
    const raw = await taskStore.get(id); // can be any of the shapes
    task.value = normalizeTask(raw);
    show();
  } catch (e) {
    toast(
      e?.response?.data?.message || e.message || "Failed to open task",
      "error"
    );
  } finally {
    loading.value = false;
  }
}

async function copyLink() {
  try {
    const text = permalink.value;
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    copied.value = true;
    setTimeout(() => (copied.value = false), 900);
  } catch {
    toast("Could not copy link", "error");
  }
}

async function markCompleted() {
  if (!task.value?.id || isCompleted.value) return;
  try {
    completing.value = true;
    await taskStore.complete(task.value.id);
    task.value = { ...task.value, status: "Completed" };
    await taskStore.list();
    toast("Task marked as completed", "success");
  } catch (e) {
    toast(
      e?.response?.data?.message || e.message || "Failed to complete task",
      "error"
    );
  } finally {
    completing.value = false;
  }
}

defineExpose({ open, hide });
</script>

<template>
  <div class="modal fade" id="view_todo" tabindex="-1">
    <div
      class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable"
    >
      <div class="modal-content">
        <!-- Header -->
        <div class="modal-header bg-dark text-white align-items-center">
          <div class="d-flex align-items-center gap-2">
            <h4 class="modal-title text-white mb-0">
              {{ task?.title || "Task" }}
            </h4>

            <!-- Optional urgency chip -->
            <span
              v-if="task?.priority === 'High'"
              class="badge badge-danger d-inline-flex align-items-center"
            >
              <i class="ti ti-square me-1"></i>Urgent
            </span>

            <span
              v-if="task?.priority === 'High' || task?.status === 'Pending'"
            >
              <i class="ti ti-star-filled text-warning"></i>
            </span>
          </div>

          <div class="d-flex align-items-center gap-2">
            <button
              class="btn btn-sm btn-light d-inline-flex align-items-center gap-2"
              @click="copyLink"
            >
              <i class="ti ti-link"></i>
              <span v-if="!copied">Copy link</span>
              <span v-else>Copied!</span>
            </button>

            <button
              class="btn btn-sm btn-success d-inline-flex align-items-center gap-2"
              @click="markCompleted"
              :disabled="isCompleted || completing"
              title="Mark as completed"
            >
              <span
                v-if="completing"
                class="spinner-border spinner-border-sm"
              ></span>
              <i v-else class="ti ti-check"></i>
              {{ isCompleted ? "Completed" : "Mark as completed" }}
            </button>

            <button
              type="button"
              class="btn-close custom-btn-close bg-transparent fs-16 text-white position-static"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <i class="ti ti-x"></i>
            </button>
          </div>
        </div>

        <!-- Body -->
        <div class="modal-body">
          <div v-if="loading" class="py-4 text-center">
            <span class="spinner-border spinner-border-sm me-2"></span>Loading…
          </div>

          <template v-else>
            <h5 class="mb-2">Task Details</h5>

            <!-- Top facts -->
            <div class="border rounded mb-3 p-3">
              <div class="row row-gap-3">
                <div class="col-md-4">
                  <div class="text-center">
                    <span class="d-block mb-1 text-muted small"
                      >Created On</span
                    >
                    <p class="text-dark mb-0">{{ createdPretty }}</p>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="text-center">
                    <span class="d-block mb-1 text-muted small">Due Date</span>
                    <p class="text-dark mb-0">{{ duePretty }}</p>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="text-center">
                    <span class="d-block mb-1 text-muted small">Status</span>
                    <span
                      class="badge d-inline-flex align-items-center"
                      :class="{
                        'badge-soft-success': task?.status === 'Completed',
                        'badge-secondary-transparent':
                          task?.status === 'Pending',
                        'badge-info-transparent': task?.status === 'Inprogress',
                        'badge-soft-pink': task?.status === 'Onhold',
                      }"
                    >
                      <i class="fas fa-circle fs-6 me-1"></i
                      >{{ task?.status || "—" }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Description -->
            <div class="mb-3">
              <h5 class="mb-2">Description</h5>
              <div v-if="task?.description" v-html="task.description"></div>
              <p v-else class="text-muted">No description provided.</p>
            </div>

            <!-- Tags -->
            <div class="mb-3">
              <h5 class="mb-2">Tags</h5>
              <div
                v-if="task?.tags?.length"
                class="d-flex align-items-center flex-wrap gap-2"
              >
                <span
                  v-for="t in task.tags"
                  :key="t"
                  class="badge bg-light text-dark rounded-pill"
                  >{{ t }}</span
                >
              </div>
              <div v-else class="text-muted">No tags.</div>
            </div>

            <!-- Assignees -->
            <div>
              <h5 class="mb-2">Assignee</h5>

              <!-- Primary: render from assignee objects -->
              <div
                v-if="task?.assignees?.length"
                class="avatar-list-stacked avatar-group-sm"
              >
                <span
                  v-for="a in task.assignees"
                  :key="a.id"
                  class="avatar avatar-rounded"
                  :title="
                    [a.first_name || '', a.last_name || '']
                      .filter(Boolean)
                      .join(' ')
                  "
                >
                  <img
                    class="border border-white"
                    :src="
                      a.avatar_url ||
                      require('@/assets/img/profiles/avatar-02.jpg')
                    "
                    alt="assignee"
                  />
                </span>
              </div>

              <!-- Fallback: render from assignee_avatars array -->
              <div
                v-else-if="task?.assignee_avatars?.length"
                class="avatar-list-stacked avatar-group-sm"
              >
                <span
                  v-for="(av, i) in task.assignee_avatars"
                  :key="i"
                  class="avatar avatar-rounded"
                >
                  <img
                    class="border border-white"
                    :src="av || require('@/assets/img/profiles/avatar-02.jpg')"
                    alt="assignee"
                  />
                </span>
              </div>

              <div v-else class="text-muted small">No assignees.</div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-dialog-scrollable .modal-content {
  max-height: calc(100vh - 3.5rem);
}
.badge-soft-success {
  background: #eaf7ef;
  color: #1b6f3c;
}
.badge-soft-pink {
  background: #fff1f5;
  color: #b81e5b;
}
</style>
